import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { projectService, workspaceService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { type ProjectFormData } from '@/lib/validations/project'
import { toast } from 'sonner'
import type { Workspace, Project, User } from '@/types'
import { PageContainer, PageHeader, LoadingState } from '@/components/layout'
import { Priority, Horizon } from '@/types/enums'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { ProjectForm } from '@/components/projects/ProjectForm'

export default function ProjectEditPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  const loadUsers = useCallback(async () => {
    if (!user) return

    try {
      const allUsers = await userService.getAll()
      setAvailableUsers(allUsers)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Failed to load users')
    }
  }, [user])

  const loadData = useCallback(
    async (id: string) => {
      if (!user) return

      try {
        setInitialLoading(true)

        // Load project
        const projectData = await projectService.getById(id)

        if (!projectData) {
          throw new Error('Project not found')
        }

        setProject(projectData)

        // Check permissions
        if (user.role !== 'SuperAdmin' && projectData.creator.id !== user.id) {
          toast.error('You do not have permission to edit this project')
          navigate(`/projects/${id}`)
          return
        }

        // Load workspaces
        const allWorkspaces = await workspaceService.getAll()
        const userWorkspaces = allWorkspaces.filter(
          (workspace) =>
            workspace.creator.id === user.id || workspace.users.some((u) => u.id === user.id)
        )
        setWorkspaces(userWorkspaces)
      } catch {
        toast.error('Failed to load project')
        navigate('/projects')
      } finally {
        setInitialLoading(false)
      }
    },
    [user, navigate]
  )

  useEffect(() => {
    if (projectId) {
      loadData(projectId)
    }
    loadUsers()
  }, [projectId, loadData, loadUsers])

  const onSubmit = async (data: ProjectFormData) => {
    if (!user || !project || !projectId) return

    setLoading(true)
    try {
      const workspace = workspaces.find((w) => w.id === data.workspaceId)
      if (!workspace) {
        toast.error('Selected workspace not found')
        return
      }

      // Get the users for the project
      const { userService } = await import('@/services')
      const projectUsers = data.userIds ? await userService.getByIds(data.userIds) : []

      const updateData: Partial<Project> = {
        name: data.name,
        description: data.description,
        workspace,
        users: projectUsers,
        status: data.status,
      }

      if (data.priority) {
        updateData.priority = data.priority
      }
      if (data.horizon) {
        updateData.horizon = data.horizon
      }

      await projectService.update(projectId, updateData)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Project updated successfully')
      navigate(`/projects/${projectId}`)
    } catch {
      toast.error('Failed to update project')
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading project..." />
      </PageContainer>
    )
  }

  if (!project) {
    return (
      <PageContainer maxWidth="full">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Project not found</p>
            <Button className="mt-4" onClick={() => navigate('/projects')}>
              Back to Projects
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title={project.name} titlePrefix="Editing Project:" />

      <ProjectForm
        mode="edit"
        defaultValues={{
          name: project.name,
          description: project.description,
          workspaceId: project.workspace.id,
          userIds: project.users.map((u) => u.id),
          status: project.status,
          priority: project.priority || Priority.None,
          horizon: project.horizon || Horizon.None,
        }}
        workspaces={workspaces}
        availableUsers={availableUsers}
        selectedUsers={project.users}
        onSubmit={onSubmit}
        onCancel={() => navigate(`/projects/${projectId}`)}
        submitText={loading ? 'Saving...' : 'Save Changes'}
        loading={loading}
        title="Project Details"
        description="Update the project details below"
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
